import React, { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { getCookie, setCookie } from '../utils/cookies';
import './CookieConsentBanner.css';

/**
 * #3131 — a necessary-cookies NOTICE, deliberately not a consent request.
 *
 * HofSync sets no trackers: there is no analytics, tag manager, or tracking
 * pixel anywhere in the app. Everything it does store — the BFF session cookie,
 * `csrf_token`, the two cookies below, and a handful of functional
 * `localStorage` entries — is strictly necessary or explicitly user-requested,
 * and therefore needs no consent under TDDDG §25(2).
 *
 * That is precisely why this is worded as a notice ("Verstanden") rather than a
 * consent request ("Ich stimme zu"): an acknowledge-only bar with no reject
 * button is a dark pattern WHEN non-essential storage exists. Here none does,
 * so acknowledge-only is defensible — but only while that stays true.
 *
 * ⚠️ The moment any analytics lands, this component is no longer compliant and
 * must gain a genuine reject control and per-category granularity.
 *
 * Patterned on `ImpersonationBanner.tsx`: a plain `React.FC` with one
 * hand-rolled stylesheet and no design-system import. Adding a design-system
 * component for this would force a DS build + version bump for a single-app
 * concern.
 */

/** Records that the notice has been seen. Not a consent record — see above. */
export const CONSENT_COOKIE = 'hofsync_cookie_consent';

/** Opaque marker value; only presence is ever checked. */
export const CONSENT_COOKIE_VALUE = 'necessary';

/** 12 months, matching the language cookie. */
export const CONSENT_COOKIE_DAYS = 365;

const CookieConsentBanner: React.FC = () => {
  const { t } = useTranslation();

  // Read once on mount. A cookie that is present but empty still counts as
  // acknowledged, which is why this tests against `null` rather than truthiness.
  const [acknowledged, setAcknowledged] = useState(() => getCookie(CONSENT_COOKIE) !== null);

  if (acknowledged) return null;

  const acknowledge = () => {
    setCookie(CONSENT_COOKIE, CONSENT_COOKIE_VALUE, { days: CONSENT_COOKIE_DAYS });
    setAcknowledged(true);
  };

  return (
    <div
      className="cookie-consent-banner"
      role="region"
      aria-label={t('cookieNotice.ariaLabel')}
      data-testid="cookie-consent-banner"
    >
      <p className="cookie-consent-banner__text">
        {t('cookieNotice.message')}{' '}
        <Link
          to="/privacy"
          className="cookie-consent-banner__link"
          data-testid="cookie-consent-privacy-link"
        >
          {t('cookieNotice.privacyLink')}
        </Link>
      </p>
      <button
        type="button"
        className="cookie-consent-banner__ack"
        onClick={acknowledge}
        data-testid="cookie-consent-ack"
      >
        {t('cookieNotice.acknowledge')}
      </button>
    </div>
  );
};

export default CookieConsentBanner;
